/**
 * CRDT socket framing for this compiler (FILE_SYNC_PROTOCOL):
 *   sync_step1:  [0x00][0x00] + length-prefixed state vector (empty vector is [0x00][0x00])
 *   sync_update: [0x00][0x02] + raw Yjs update. No varuint length prefix.
 *   Awareness:   [0x01] + raw awareness payload
 *   Auth:        [0x02] + payload
 * Incoming step1 from the server is still y-protocols length-prefixed.
 */

import * as encoding from "lib0/encoding"
import * as decoding from "lib0/decoding"
import * as syncProtocol from "y-protocols/sync"
import type * as Y from "yjs"

export const Y_MESSAGE_SYNC = 0
export const Y_MESSAGE_AWARENESS = 1
export const Y_MESSAGE_AUTH = 2
export const Y_MESSAGE_QUERY_AWARENESS = 3
/** Some backends use type 4 for document/sync payloads (observed in multi-client relays). */
export const Y_MESSAGE_SYNC_ALT = 4

export const Y_SYNC_STEP1 = syncProtocol.messageYjsSyncStep1 // 0
export const Y_SYNC_STEP2 = syncProtocol.messageYjsSyncStep2 // 1
export const Y_SYNC_UPDATE = syncProtocol.messageYjsUpdate // 2

export type DecodedYMessage = {
  mainType: number
  subType: number
  data: Uint8Array
}

export function encodeVarUint(value: number): Uint8Array {
  const encoder = encoding.createEncoder()
  encoding.writeVarUint(encoder, value)
  return encoding.toUint8Array(encoder)
}

export function decodeVarUint(
  buffer: Uint8Array,
  offset: number,
): { value: number; nextOffset: number } | null {
  try {
    const decoder = decoding.createDecoder(buffer)
    decoder.pos = offset
    const value = decoding.readVarUint(decoder)
    return { value, nextOffset: decoder.pos }
  } catch {
    return null
  }
}

/** Outbound sync frame using y-protocols length-prefixed payloads. */
export function encodeSyncMessage(subType: number, data: Uint8Array): Uint8Array {
  const encoder = encoding.createEncoder()
  encoding.writeVarUint(encoder, Y_MESSAGE_SYNC)
  encoding.writeVarUint(encoder, subType)
  encoding.writeVarUint8Array(encoder, data)
  return encoding.toUint8Array(encoder)
}

/** Outbound sync_step1. Empty state vector is exactly [0x00][0x00]. */
export function encodeSyncStep1FromDoc(doc: Y.Doc): Uint8Array {
  const encoder = encoding.createEncoder()
  encoding.writeVarUint(encoder, Y_MESSAGE_SYNC)
  syncProtocol.writeSyncStep1(encoder, doc)
  const framed = encoding.toUint8Array(encoder)
  // y-protocols writes [0x00][0x00][0x01][0x00] for an empty vector.
  // The compiler handshake is the two type bytes and nothing else.
  if (
    framed.byteLength === 4 &&
    framed[0] === Y_MESSAGE_SYNC &&
    framed[1] === Y_SYNC_STEP1 &&
    framed[2] === 1 &&
    framed[3] === 0
  ) {
    return new Uint8Array([Y_MESSAGE_SYNC, Y_SYNC_STEP1])
  }
  return framed
}

/**
 * Outbound sync_update: [0x00][0x02] + raw Yjs bytes.
 * A varuint length before the update is parsed as the start of the delta.
 */
export function encodeSyncUpdate(update: Uint8Array): Uint8Array {
  const framed = new Uint8Array(2 + update.byteLength)
  framed[0] = Y_MESSAGE_SYNC
  framed[1] = Y_SYNC_UPDATE
  framed.set(update, 2)
  return framed
}

/**
 * Turn a y-protocols sync_step2 reply ([0x00][0x01][len][update]) into the
 * compiler sync_update frame ([0x00][0x02] + raw update).
 */
export function backendSyncUpdateFromYProtocolsReply(reply: Uint8Array): Uint8Array {
  try {
    const decoder = decoding.createDecoder(reply)
    const main = decoding.readVarUint(decoder)
    if (main !== Y_MESSAGE_SYNC) return reply
    const sub = decoding.readVarUint(decoder)
    if (sub !== Y_SYNC_STEP2 && sub !== Y_SYNC_UPDATE) return reply
    return encodeSyncUpdate(decoding.readVarUint8Array(decoder))
  } catch {
    return reply
  }
}

/** Outbound awareness frame: [0x01][awareness_binary_payload] (backend protocol). */
export function encodeAwarenessMessage(data: Uint8Array): Uint8Array {
  const msg = new Uint8Array(1 + data.byteLength)
  msg[0] = Y_MESSAGE_AWARENESS
  msg.set(data, 1)
  return msg
}

/**
 * Decode a WebSocket binary frame.
 * Sync payloads are length-prefixed (y-protocols). Awareness stays raw after 0x01.
 */
export function decodeYMessage(buffer: Uint8Array): DecodedYMessage | null {
  if (buffer.byteLength === 0) return null

  // Awareness/Auth: single-byte type prefix + raw payload (backend protocol).
  const firstByte = buffer[0]
  if (firstByte === Y_MESSAGE_AWARENESS || firstByte === Y_MESSAGE_AUTH) {
    return { mainType: firstByte, subType: -1, data: buffer.slice(1) }
  }

  try {
    const decoder = decoding.createDecoder(buffer)
    const mainType = decoding.readVarUint(decoder)

    if (mainType === Y_MESSAGE_SYNC) {
      const subType = decoding.readVarUint(decoder)
      let data: Uint8Array
      try {
        // Standard y-protocols: length-prefixed payload.
        data = decoding.readVarUint8Array(decoder)
      } catch {
        // Legacy: raw payload with no length prefix (pre-fix clients).
        data = decoding.readTailAsUint8Array(decoder)
      }
      return { mainType, subType, data }
    }

    return {
      mainType,
      subType: -1,
      data: decoding.readTailAsUint8Array(decoder),
    }
  } catch {
    return null
  }
}

/**
 * Apply a full WS frame that starts with messageSync (0) using y-protocols.
 * Returns the sync subtype and any reply bytes to send (may be empty / sync header only).
 */
export function readSyncMessageFromFrame(
  buffer: Uint8Array,
  doc: Y.Doc,
  transactionOrigin: unknown,
): { syncType: number; reply: Uint8Array | null } {
  const decoder = decoding.createDecoder(buffer)
  const mainType = decoding.readVarUint(decoder)
  if (mainType !== Y_MESSAGE_SYNC) {
    throw new Error(`Expected sync message type 0, got ${mainType}`)
  }

  const replyEncoder = encoding.createEncoder()
  encoding.writeVarUint(replyEncoder, Y_MESSAGE_SYNC)
  const syncType = syncProtocol.readSyncMessage(
    decoder,
    replyEncoder,
    doc,
    transactionOrigin,
  )
  const reply = encoding.toUint8Array(replyEncoder)
  // Only send a reply when readSyncMessage wrote more than the messageSync byte
  // (i.e. SyncStep1 → state the peer is missing).
  const hasReply = reply.byteLength > 1
  if (!hasReply) return { syncType, reply: null }
  if (syncType === Y_SYNC_STEP1) {
    return { syncType, reply: backendSyncUpdateFromYProtocolsReply(reply) }
  }
  return { syncType, reply }
}

export function syncSubtypeLabel(subType: number): string {
  if (subType === Y_SYNC_STEP1) return "sync_step1"
  if (subType === Y_SYNC_STEP2) return "sync_step2"
  if (subType === Y_SYNC_UPDATE) return "sync_update"
  return `sync_subtype_${subType}`
}

export function messageTypeLabel(mainType: number): string {
  if (mainType === Y_MESSAGE_SYNC) return "sync"
  if (mainType === Y_MESSAGE_AWARENESS) return "awareness"
  if (mainType === Y_MESSAGE_AUTH) return "auth"
  if (mainType === Y_MESSAGE_QUERY_AWARENESS) return "query_awareness"
  if (mainType === Y_MESSAGE_SYNC_ALT) return "sync_alt"
  return `msg_${mainType}`
}

/**
 * Backend often relays document updates as bare `varUint8Array(update)`
 * (strips the outer `[0][2]` sync wrapper). Detect and unwrap that form.
 * Also accepts a raw Yjs update with no length prefix.
 */
export function extractRelayedYjsUpdate(payload: Uint8Array): Uint8Array | null {
  if (payload.byteLength === 0) return null

  // Length-prefixed: first varUint is length and exactly consumes the rest.
  try {
    const decoder = decoding.createDecoder(payload)
    const len = decoding.readVarUint(decoder)
    if (len > 0 && decoder.pos + len === payload.byteLength) {
      return decoding.readUint8Array(decoder, len)
    }
  } catch {
    /* not length-prefixed */
  }

  // Raw Yjs update (no wrapper). Heuristic: not a known tiny control message type.
  if (payload[0] > 4) {
    return payload
  }

  return null
}

/**
 * True when `body` is a length-prefixed blob that exactly fills the buffer
 * (y-protocols sync_step2 / sync_update payload shape).
 */
export function isExactLengthPrefixedBlob(body: Uint8Array): boolean {
  if (body.byteLength < 2) return false
  try {
    const decoder = decoding.createDecoder(body)
    const len = decoding.readVarUint(decoder)
    return len > 0 && decoder.pos + len === body.byteLength
  } catch {
    return false
  }
}

/**
 * Backend sometimes replies to sync_step1 as `[1][len][update…]` (sync_step2
 * subtype only) instead of `[0][1][len][update…]`. Rebuild the full sync frame.
 */
export function remapBareSyncStepToFramed(payload: Uint8Array): Uint8Array | null {
  if (payload.byteLength < 3) return null
  const subType = payload[0]
  if (subType !== Y_SYNC_STEP2 && subType !== Y_SYNC_UPDATE) return null
  const body = payload.slice(1)
  if (!isExactLengthPrefixedBlob(body)) return null
  const framed = new Uint8Array(payload.byteLength + 1)
  framed[0] = Y_MESSAGE_SYNC
  framed.set(payload, 1)
  return framed
}

export function bytesPreview(bytes: Uint8Array, count = 10): number[] {
  return Array.from(bytes.slice(0, count))
}

export type HistoryPayloadKind = "empty" | "raw_yjs" | "framed_sync" | "framed_non_sync"

export type HistoryPayloadAnalysis = {
  kind: HistoryPayloadKind
  /** Bytes to pass to Y.applyUpdate (may equal input when raw). */
  yjsPayload: Uint8Array
  hadFraming: boolean
  mainType?: number
  subType?: number
}

/**
 * Classify bytes from crdt-history storage.
 * Backend should persist raw Yjs updates; if it persists full WS frames, first bytes are often [0, 2, …].
 */
export function analyzeHistoryPayload(bytes: Uint8Array): HistoryPayloadAnalysis {
  if (bytes.byteLength === 0) {
    return { kind: "empty", yjsPayload: bytes, hadFraming: false }
  }

  try {
    const decoder = decoding.createDecoder(bytes)
    const main = decoding.readVarUint(decoder)
    if (main !== Y_MESSAGE_SYNC) {
      return { kind: "raw_yjs", yjsPayload: bytes, hadFraming: false }
    }
    const sub = decoding.readVarUint(decoder)
    if (
      sub !== Y_SYNC_STEP1 &&
      sub !== Y_SYNC_STEP2 &&
      sub !== Y_SYNC_UPDATE
    ) {
      return { kind: "raw_yjs", yjsPayload: bytes, hadFraming: false }
    }
    const yjsPayload = decoding.readVarUint8Array(decoder)
    return {
      kind: "framed_sync",
      yjsPayload,
      hadFraming: true,
      mainType: main,
      subType: sub,
    }
  } catch {
    return { kind: "raw_yjs", yjsPayload: bytes, hadFraming: false }
  }
}

/** Unwrap WS framing when history stored a full [msgType][subType][payload] packet. */
export function yjsPayloadForReplay(storedBytes: Uint8Array): HistoryPayloadAnalysis {
  return analyzeHistoryPayload(storedBytes)
}
